import { FormattedCurrency, useI18n } from '@dustin-web/microsite-components/src/i18n'
import QuantitySelector, { SIZE } from '@dustin-web/microsite-components/src/quantity-selector'
import { getRelativeProductImageUrl } from '@dustin-web/microsite-components/src/utils/urls'
import AddToCartSwitcher from '@dustin-web/product-components/src/add-to-cart-button'
import { useEffect, useState } from 'react'
import Image from '@dustin-web/microsite-components/src/image/image'
import { GetProductAccessoriesQuery } from '@cartsite-app/src/graphql/generated/graphql-types'
import ProductAvailability from '@dustin-web/product-components/src/product-availability'
import { useResponsive } from '@dustin-web/microsite-components/src/hooks'

type DependentAccessories = NonNullable<Accessory>['dependentAccessories'][0]
type Accessory = NonNullable<GetProductAccessoriesQuery>['accessories']

type Props = {
  accessory: DependentAccessories
  mainProductErpIdentifier: string | null
  mainProductQuantity: number
  accessoryQuantity: number
  digitalDataIndex?: number
  parentCartItemId?: number
}

const CartItemAccessoryCard = ({
  accessory,
  mainProductErpIdentifier,
  mainProductQuantity,
  accessoryQuantity,
  digitalDataIndex,
  parentCartItemId,
}: Props) => {
  const { translate } = useI18n()
  const [quantity, setQuantity] = useState(1)
  const [maxQuantity, setMaxQuantity] = useState(accessory.maxQuantityPerOrder!)
  const { breakpoints } = useResponsive()

  useEffect(() => {
    // eslint-disable-line
    if (accessory.maxQuantityPerOrder! > 0 && accessoryQuantity > mainProductQuantity) {
      const maxQuantity = accessory.maxQuantityPerOrder! - accessoryQuantity
      setMaxQuantity(maxQuantity)
      return
    }

    const maxQuantity = mainProductQuantity - accessoryQuantity
    setMaxQuantity(maxQuantity)
  })

  if (!accessory?.price) {
    return null
  }

  const price = accessory.price

  return (
    <>
      <div className="c-shoppingcart__item-image">
        <Image
          src={getRelativeProductImageUrl(
            accessory.primaryImageId ?? '',
            360,
            260,
            `${accessory.manufacturerName} ${accessory.displayName} ${accessory.displaySpecifications}`
          )}
          alt={`${accessory.manufacturerName} ${accessory.displayName} ${accessory.displaySpecifications}`}
          width={75}
          height={60}
          priority={true}
        />
      </div>
      <div className="c-shoppingcart__item-info">
        <div className="c-shoppingcart__item-title">
          <h3>
            <a href={`/product/${accessory.productErpIdentifier}`} className="a-stripped">
              {accessory.displayName}
            </a>
          </h3>
        </div>
        <div className="c-shoppingcart__item-accessory-price-wrapper">
          {price.isCampaign && (
            <>
              <div className="c-shoppingcart__item-accessory-price">
                <div className="c-price c-price--original">
                  <FormattedCurrency amount={price.originalPrice} />
                </div>
                <small className="c-shoppingcart__item-accessory-price-label u-color-mute">
                  {translate('Cart__OriginalPrice')}
                </small>
              </div>

              <div className="c-shoppingcart__item-accessory-price c-shoppingcart__item-accessory-price--save">
                <div>
                  <FormattedCurrency amount={price.originalPrice - price.price} />
                </div>
                <small className="c-shoppingcart__item-accessory-price-label u-color-mute">
                  {translate('Cart__YouSave')}
                </small>
              </div>
            </>
          )}
          <div className="c-shoppingcart__item-accessory-price">
            <div className={`c-price ${price.isCampaign ? 'c-price--campaign' : ''} `}>
              <FormattedCurrency amount={price.price} />
            </div>
            {price.isCampaign && (
              <small className="c-shoppingcart__item-accessory-price-label u-color-mute">
                {translate('Cart__CampaignPrice')}
              </small>
            )}
          </div>
        </div>
      </div>
      <div className="c-shoppingcart__item-actions">
        {breakpoints.min.MD && (
          <div className="u-mr-8 flex-grow-1">
            <ProductAvailability availability={accessory.availability || undefined} />
          </div>
        )}

        <div className="u-mr-8">
          <QuantitySelector
            productName={accessory.displayName}
            maxQuantity={maxQuantity || 0}
            minQuantity={accessory.minQuantityPerOrder || 1}
            setQuantity={setQuantity}
            quantity={quantity}
            debounce={true}
            size={SIZE.SMALL}
            displayValidationMessage={false}
          />
        </div>

        <div className="d-flex justify-content-end">
          <AddToCartSwitcher
            product={accessory}
            quantity={quantity}
            isAd={false}
            mainProductErpIdentifier={mainProductErpIdentifier}
            parentCartItemId={parentCartItemId}
            ticket={''}
            disabled={
              quantity > maxQuantity ||
              (accessory.maxQuantityPerOrder! > 0 &&
                accessoryQuantity === accessory.maxQuantityPerOrder)
            }
            digitalDataOrigin="shoppingCartAccessories"
            digitalDataIndex={digitalDataIndex}
            preventSetLastUpdatedProductErpIdentifier={true}
          />
        </div>
      </div>
    </>
  )
}

export default CartItemAccessoryCard
